import { useState } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Loader2, Send, Eye, X } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { useKaartToken, GRIJS } from "@/lib/kaartToken";
import OpenOpnieuw from "./OpenOpnieuw";
import { useTaal } from "@/contexts/TaalContext";

/** Een mail beantwoorden, in twee stappen die niet samen te voegen zijn.
 *
 *  R57 fase 3, en dit scherm draagt Franks EERSTE eis van 26 augustus 2026:
 *  bevestiging op glas MET DE EXACTE TEKST ERVOOR.
 *
 *  DRIE TOESTANDEN EN ZE ZIJN NIET INWISSELBAAR:
 *
 *    SCHRIJVEN   Frank typt of past aan. Er is nog niets opgeslagen en er kan
 *                niets vertrekken.
 *    NALEZEN     De tekst staat er ALLEEN-LEZEN, precies zoals hij is
 *                opgeslagen, met aan wie en waarover. Dit is het moment van de
 *                bevestiging, en het is een eigen stap omdat een knop naast een
 *                invoervak te makkelijk per ongeluk wordt geraakt.
 *    VERSTUURD   Er is iets de deur uit dat niet terugkomt. Dat staat er dan
 *                ook zo.
 *
 *  DE TEKST IN DE NALEESSTAP KOMT VAN DE SERVER TERUG EN NIET UIT DIT SCHERM.
 *  Dat is geen omweg maar de hele bedoeling: wat hier te lezen staat is wat er
 *  is OPGESLAGEN, en dat is precies wat de verzendroute accepteert. Zou dit
 *  scherm zijn eigen tekstvak tonen, dan bevestigt Frank wat het scherm zegt in
 *  plaats van wat er vertrekt, en dan is de bevestiging een gewoonte in plaats
 *  van een eigenschap.
 *
 *  ER WORDT NIETS AUTOMATISCH VERSTUURD. Geen model, geen agent en geen
 *  achtergrondtaak raakt deze twee knoppen; dit zijn handelingen van Frank.
 */
export default function MailAntwoord({ berichtId, afzender, onderwerp, opening = "", bestaand = null }) {
  const { t } = useTaal();
  // W8b: een concept dat een agent al opstelde, uit zijn spoor. De kaart begint dan
  // bij het nalezen, met het token van `/toon`; versturen is dezelfde knop als altijd.
  const [tekst, setTekst] = useState(opening || bestaand?.tekst || "");
  const [concept, setConcept] = useState(bestaand);
  const [bezig, setBezig] = useState(false);
  // BB1 (GO Frank 4 nov): het concept dat Frank verliet met "Terug naar bewerken". De
  // volgende teruglezing wijst ernaar, zodat een bewerkt concept zijn herkomst houdt.
  const [vorige, setVorige] = useState(null);
  // WF-40 (post a): het token van dit concept, zolang het te bevestigen is.
  const { token, vernieuw, vervallen, laatVervallen, reden } = useKaartToken(
    concept?.id ? `${API}/mail/reply/${concept.id}/toon` : "",
    Boolean(concept?.id && !concept.verstuurd && !concept.vertrekt_op));

  const lees_na = async () => {
    setBezig(true);
    try {
      const r = await axios.post(`${API}/mail/reply/prepare`, {
        bericht_id: berichtId || bestaand?.bericht_id, tekst,
        ...(vorige ? { vorig_concept_id: vorige } : {}),
      });
      setConcept(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("Het opstellen is niet gelukt"));
    } finally {
      setBezig(false);
    }
  };

  const verstuur = async () => {
    setBezig(true);
    try {
      // DE TEKST DIE MEEGAAT IS DIE VAN HET CONCEPT, dus die op het scherm
      // heeft gestaan. Hier `tekst` meesturen zou het hele onderscheid
      // opheffen: dan bevestigt Frank het invoervak en niet wat er vertrekt.
      const r = await axios.post(`${API}/mail/reply/${concept.id}/send`, {
        tekst: concept.tekst,
        token,
      });
      setConcept(r.data);
      toast.success(t("Verstuurd"));
    } catch (e) {
      const tekst = e.response?.data?.detail || t("Het versturen is niet gelukt");
      toast.error(tekst);
      // FIX 4: geen stil nieuw token; alleen "Open opnieuw". Punt 5: de reden blijft bewaard.
      laatVervallen(tekst);
    } finally {
      setBezig(false);
    }
  };

  if (concept?.verstuurd) {
    return (
      <div className="border rounded-lg p-3 space-y-1" data-testid="mail-verstuurd">
        <p className="text-xs font-medium">{t("Verstuurd naar {aan}", { aan: concept.aan })}</p>
        <p className="text-xs text-muted-foreground">
          {concept.onderwerp} - {t("dit is nu bij de ontvanger en kan niet worden teruggehaald.")}
        </p>
        {concept.vorig_concept_id && (
          <p className="text-xs text-muted-foreground" data-testid="mail-bewerkt">
            {t("Bewerkt verstuurd: het eerdere concept is bewaard.")}
          </p>
        )}
      </div>
    );
  }

  if (concept) {
    return (
      <div className="border rounded-lg p-3 space-y-3" data-testid="mail-nalezen">
        <div className="space-y-0.5">
          <p className="text-xs text-muted-foreground">
            {t("Aan {aan} - {onderwerp}", { aan: concept.aan, onderwerp: concept.onderwerp })}
          </p>
          <p className="text-xs text-muted-foreground">
            {t("Dit is precies wat er verstuurd wordt. Er kan niets anders uit.")}
          </p>
        </div>
        {/* ALLEEN-LEZEN EN GEEN INVOERVAK. Wat hier staat komt van de server
            terug; het is de opgeslagen tekst en niet die van dit scherm. */}
        <pre className="text-xs whitespace-pre-wrap border rounded p-2 bg-muted/40"
             data-testid="mail-exacte-tekst">{concept.tekst}</pre>
        {/* Punt 5: na herladen staat de bewaarde weigering er nog; de toast is dan weg. */}
        {vervallen && reden && (
          <p className="text-xs text-[hsl(var(--destructive))]" data-testid="mail-foutmelding">{reden}</p>
        )}
        <div className="flex gap-2">
          <Button size="sm" className={vervallen ? GRIJS : ""} onClick={verstuur} disabled={bezig || !token}
                  data-testid="mail-verstuur-btn">
            {bezig ? <Loader2 className="h-3 w-3 animate-spin mr-1" />
                   : <Send className="h-3 w-3 mr-1" />}
            {t("Precies deze tekst versturen")}
          </Button>
          {vervallen && <OpenOpnieuw onClick={vernieuw} bezig={bezig} />}
          <Button size="sm" variant="outline" disabled={bezig}
                  onClick={() => { setVorige(concept.id); setConcept(null); }} data-testid="mail-terug-btn">
            <X className="h-3 w-3 mr-1" />
            {t("Terug naar bewerken")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="border rounded-lg p-3 space-y-2" data-testid="mail-schrijven">
      <p className="text-xs text-muted-foreground">
        {t("Antwoord aan {afzender} - {onderwerp}", { afzender, onderwerp })}
      </p>
      <textarea
        className="w-full text-xs border rounded p-2 min-h-24 bg-background"
        value={tekst}
        onChange={e => setTekst(e.target.value)}
        data-testid="mail-invoer"
        placeholder={t("Schrijf je antwoord. Er gaat niets weg tot je het hebt teruggelezen.")}
      />
      <Button size="sm" onClick={lees_na} disabled={bezig || !tekst.trim()}
              data-testid="mail-nalezen-btn">
        {bezig ? <Loader2 className="h-3 w-3 animate-spin mr-1" />
               : <Eye className="h-3 w-3 mr-1" />}
        {t("Teruglezen")}
      </Button>
    </div>
  );
}
